import { env, isConfigured } from "./env";
import { requireSupabase } from "./supabase";
import type { AdminDashboard, BindingCandidate, EventType, LineIdentity, PositionEvidence, TodayStatus, WorkMode } from "./types";

type RecordEventInput = PositionEvidence & {
  eventType: EventType;
  workMode: WorkMode;
  note?: string;
  outsideReason?: string;
  workType?: string;
  caseId?: string;
  customerProject?: string;
};

async function attendanceRequest<T>(identity: LineIdentity, action: string, payload: Record<string, unknown> = {}): Promise<T> {
  if (!isConfigured) throw new Error("Attendance backend is not configured");
  const response = await fetch(`${env.supabaseUrl}/functions/v1/attendance-api`, {
    method: "POST",
    headers: { "Content-Type": "application/json", apikey: env.supabaseAnonKey },
    body: JSON.stringify({ action, identity, ...payload }),
  });
  const result = await response.json().catch(() => ({ error: "invalid_response" }));
  if (!response.ok) throw new Error(String(result.message ?? result.error ?? `attendance_api_${response.status}`));
  return result as T;
}

export const getTodayStatus = (identity: LineIdentity) => attendanceRequest<TodayStatus>(identity, "status");
export const lookupIdentity = (identity: LineIdentity, lookup: { personnelId?: string; phone?: string }) => attendanceRequest<{ candidate: BindingCandidate }>(identity, "lookup", lookup);
export const bindIdentity = (identity: LineIdentity, profileId: string) => attendanceRequest<TodayStatus>(identity, "bind", { profileId });
export const recordAttendanceEvent = (identity: LineIdentity, input: RecordEventInput) => attendanceRequest<{ event: { id: string; serverTimestamp: string; statusLabel: string; locationName: string | null; needsReview: boolean }; today: TodayStatus }>(identity, "record_event", input);

export async function adminLogin(email: string, password: string) {
  const { error } = await requireSupabase().auth.signInWithPassword({ email, password });
  if (error) throw error;
}

export async function adminLogout() {
  await requireSupabase().auth.signOut();
}

export async function getAdminDashboard(filters: { date: string; department?: string; personnelType?: string; status?: string }) {
  const { data, error } = await requireSupabase().rpc("attendance_admin_dashboard", {
    p_date: filters.date,
    p_department: filters.department || null,
    p_personnel_type: filters.personnelType || null,
    p_status: filters.status || null,
  });
  if (error) throw error;
  return data as AdminDashboard;
}

export async function resetLineBinding(profileId: string, reason: string) {
  const { data, error } = await requireSupabase().rpc("attendance_reset_line_binding", { p_profile_id: profileId, p_reason: reason });
  if (error) throw error;
  return data;
}

export type DatabaseHealth = {
  sizeBytes: number;
  sizeMb: number;
  quotaMb: number;
  forecastThresholdMb: number;
  upgradeThresholdMb: number;
  percentUsed: number;
  status: "HEALTHY" | "FORECAST_REQUIRED" | "UPGRADE_REQUIRED";
  checkedAt: string;
};

export async function getDatabaseHealth() {
  const { data, error } = await requireSupabase().rpc("attendance_database_health");
  if (error) throw error;
  return data as DatabaseHealth;
}
